import React, { FC, useEffect, useState } from 'react'
import { Box, Grid, Paper } from '@mui/material'
import { DataGridPremium, GridColDef, useGridApiRef, gridFilterModelSelector, gridFilteredTopLevelRowCountSelector, GridPaginationModel } from '@mui/x-data-grid-premium'
import { TableToolbar } from 'src/components/ui'
import { SearchForm } from 'src/components/forms'
import { BleConnectionLog } from 'src/api'
import { SearchParamsKeys } from 'src/enums'
import { defaultRowsValuePerPage } from 'src/config'
import { BleConnectionLogColumns } from './bleConnectionLogColumns'
import { BleConnectionLogListFilters } from './bleConnectionLogListFilters'

const defaultRowsPerPageOptions = defaultRowsValuePerPage

interface BleConnectionLogListProps {
  logs: BleConnectionLog[]
  logCount: number
  isLoading: boolean
  queryParams: Record<string, string>
  setQueryParams: (params: Record<string, string>, paramsToRemove?: string[]) => void
  autoHeight?: boolean
  exportedFileName?: string
  showFilters?: boolean
}

export const BleConnectionLogList: FC<BleConnectionLogListProps> = ({
  logCount,
  logs,
  isLoading,
  setQueryParams,
  queryParams,
  autoHeight = false,
  exportedFileName,
  showFilters = true
}) => {
  const apiRef = useGridApiRef()
  const columns: GridColDef<BleConnectionLog>[] = [
    BleConnectionLogColumns.Id,
    BleConnectionLogColumns.DeviceType,
    BleConnectionLogColumns.Step,
    BleConnectionLogColumns.Status,
    BleConnectionLogColumns.EventType,
    BleConnectionLogColumns.User,
    BleConnectionLogColumns.Contractor,
    BleConnectionLogColumns.Project,
    BleConnectionLogColumns.Section,
    BleConnectionLogColumns.Probe,
    BleConnectionLogColumns.EventName,
    BleConnectionLogColumns.AppVersion,
    BleConnectionLogColumns.DeviceOS,
    BleConnectionLogColumns.OccurredAt
  ]
  const [rowCountState, setRowCountState] = useState(logCount)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    setRowCountState((prevRowCountState) => (logCount !== undefined ? logCount : prevRowCountState))
  }, [logCount, setRowCountState])

  const handleChangePaginationModel = async (pagination: GridPaginationModel): Promise<void> => {
    const { page, pageSize } = pagination
    const newPage = page + 1
    if (setQueryParams) {
      setQueryParams(
        {
          [SearchParamsKeys.Page]: `${newPage}`,
          [SearchParamsKeys.RowsPerPage]: `${pageSize}`
        },
        [SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]
      )
    }
  }

  const handleChangeState = async () => {
    const filterModel = gridFilterModelSelector(apiRef.current.state)
    if (filterModel.items.length > 0 && filterModel.items[0].value) {
      const visibleRowCount = gridFilteredTopLevelRowCountSelector(apiRef.current.state)
      if (visibleRowCount) setRowCountState(visibleRowCount)
    } else {
      setRowCountState(logCount)
    }
  }

  const page = queryParams[SearchParamsKeys.Page] ? parseInt(queryParams[SearchParamsKeys.Page], 10) - 1 : 0
  const pageSize = queryParams[SearchParamsKeys.RowsPerPage] ? parseInt(queryParams[SearchParamsKeys.RowsPerPage], 10) : defaultRowsPerPageOptions[0]

  if (!isMounted) return null

  return (
    <Box height="100%">
      {showFilters && (
        <Grid container item flexDirection="column" justifyContent="space-between" mb={2}>
          <Box mb={2}>
            <SearchForm />
          </Box>
          <BleConnectionLogListFilters />
        </Grid>
      )}
      <Paper sx={{ flexGrow: 1, minHeight: '200px', height: '100%', maxHeight: '800px' }}>
        <DataGridPremium<BleConnectionLog>
          apiRef={apiRef}
          pagination
          autoHeight={autoHeight}
          disableRowSelectionOnClick
          rows={logs}
          columns={columns}
          rowCount={rowCountState}
          loading={isLoading}
          paginationModel={{ page, pageSize }}
          rowThreshold={2}
          columnThreshold={2}
          paginationMode="server"
          pageSizeOptions={defaultRowsPerPageOptions}
          onPaginationModelChange={handleChangePaginationModel}
          onStateChange={handleChangeState}
          localeText={{
            columnMenuSortAsc: 'Sort A-Z',
            columnMenuSortDesc: 'Sort Z-A'
          }}
          slots={{
            toolbar: () => <TableToolbar exportedFileName={exportedFileName} />
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaderTitleContainerContent': {
              paddingLeft: '5px',
              overflow: 'visible'
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1,
              fontSize: '13px',
              fontWeight: 400,
              color: 'rgba(0, 0, 0, 0.6)',
              overflow: 'visible'
            }
          }}
        />
      </Paper>
    </Box>
  )
}
